import { Schema } from 'effect';

import { toCitationAnchors } from './citation-anchors';
import { toOkraParseOutput } from './okra-parse-output';
import type { CanonicalParseResult } from '../types';

export interface EvalTaskOutputProjection {
  readonly facetId: string;
  readonly outputText?: string;
  readonly outputJson?: unknown;
  readonly tables?: readonly unknown[];
  readonly anchors?: readonly {
    readonly page: number;
    readonly text?: string;
    readonly bbox?: {
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
    };
    readonly type?: string;
  }[];
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export const EvalTaskOutputProjectionSchema = Schema.Struct({
  facetId: Schema.String,
  outputText: Schema.optionalKey(Schema.String),
  outputJson: Schema.optionalKey(Schema.Unknown),
  tables: Schema.optionalKey(Schema.Array(Schema.Unknown)),
  anchors: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        page: Schema.Int,
        text: Schema.optionalKey(Schema.String),
        bbox: Schema.optionalKey(
          Schema.Struct({
            x: Schema.Finite,
            y: Schema.Finite,
            w: Schema.Finite,
            h: Schema.Finite,
          }),
        ),
        type: Schema.optionalKey(Schema.String),
      }),
    ),
  ),
  metadata: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
});

/** Structural projection: parser-sdk intentionally does not depend on evals. */
export function toEvalTaskOutput(
  result: CanonicalParseResult,
  facetId: string,
): EvalTaskOutputProjection {
  const output = toOkraParseOutput(result);
  const anchors = toCitationAnchors(result, facetId).map((anchor) => ({
    page: anchor.pageNumber,
    ...(anchor.text === undefined ? {} : { text: anchor.text }),
    bbox: { ...anchor.bbox },
    ...(anchor.type === undefined ? {} : { type: anchor.type }),
  }));
  return {
    facetId,
    ...(output.markdown === undefined ? {} : { outputText: output.markdown }),
    outputJson: result,
    ...(output.tables === undefined ? {} : { tables: output.tables }),
    ...(anchors.length === 0 ? {} : { anchors }),
    metadata: { ...result.metadata },
  };
}
